<!-- Opportunities_Summary.vue — compact summary of a patient's active enrolled opportunities -->
<!--
  Self-contained: calls /api/opportunities/check-enrollment/ (the same source the
  Programs tab uses) and renders one quiet row per active program with its color,
  current stage, due date, and active-task count. No new backend.
-->
<template>
  <div>
    <div class="flex items-center justify-between mb-2">
      <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider">
        Enrolled Programs
      </div>
      <div class="flex items-center gap-3">
        <router-link
          v-if="patientIdentifier"
          :to="`/patients/${patientIdentifier}`"
          class="text-[11px] text-blue-500 hover:text-blue-700 transition-colors inline-flex items-center gap-1"
          title="Open full patient record"
        >
          Details
          <FontAwesomeIcon :icon="['fas', 'arrow-up-right-from-square']" class="text-[9px]" />
        </router-link>
        <button
          :disabled="loading"
          class="text-gray-300 hover:text-gray-500 transition-colors"
          title="Refresh"
          @click="loadData"
        >
          <FontAwesomeIcon :icon="['fas', 'sync']" :class="{ 'animate-spin': loading }" class="text-[11px]" />
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-4">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-gray-300 animate-spin text-sm" />
    </div>

    <!-- Empty -->
    <p v-else-if="!activePrograms.length" class="text-xs text-gray-400">
      Not enrolled in any active care management programs.
    </p>

    <!-- Programs -->
    <div v-else class="rounded-lg overflow-hidden border border-gray-100 divide-y divide-gray-100">
      <div
        v-for="p in activePrograms"
        :key="p.opportunity_id"
        class="bg-white px-3 py-2 flex items-center gap-3"
      >
        <span
          class="w-1.5 h-8 rounded-full shrink-0"
          :style="{ backgroundColor: p.metric_uicolor || '#6b7280' }"
        ></span>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-xs font-semibold text-gray-900 truncate">{{ p.metric_name }}</span>
          </div>
          <div class="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5 truncate">
            <span class="truncate">{{ currentStage(p) }}</span>
            <span v-if="dueDate(p)" class="shrink-0" :class="isOverdue(p) ? 'text-red-600 font-medium' : ''">
              · Due {{ dueDate(p) }}
            </span>
          </div>
        </div>
        <span
          v-if="p.active_task_count"
          class="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium shrink-0"
        >{{ p.active_task_count }} active</span>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { formatDueTime } from '@/utils/common';
import { checkEnrollment } from '@/services/api/opportunities';

export default {
  name: 'Opportunities_Summary',
  components: { FontAwesomeIcon },
  props: {
    patientIdentifier: { type: [String, Number], default: null },
    entityId: { type: [String, Number], default: null },
  },
  data() {
    return {
      programs: [],
      loading: false,
    };
  },
  computed: {
    activePrograms() {
      // Care Management only, with at least one active task.
      return (this.programs || []).filter(
        (p) => p.metric_category === 'Care Management' && (p.active_task_count || 0) > 0
      );
    },
  },
  watch: {
    patientIdentifier() {
      this.loadData();
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      if (!this.patientIdentifier) return;
      this.loading = true;
      try {
        const enrollmentResponse = await checkEnrollment({
          patient_identifier: this.patientIdentifier,
          entity_id: this.entityId,
          show_completed: false,
        });
        this.programs = enrollmentResponse.enrolled_programs || [];
      } catch (e) {
        this.programs = [];
      } finally {
        this.loading = false;
      }
    },
    activeTask(p) {
      // First active (status 1) or snoozed (status 4) task; fall back to first.
      const tasks = p.tasks || [];
      return (
        tasks.find((t) => t.status && (t.status.id === 1 || t.status.id === 4) && !t.is_inactive) ||
        tasks.find((t) => !t.is_inactive) ||
        null
      );
    },
    currentStage(p) {
      const t = this.activeTask(p);
      if (!t) return 'No active stage';
      const name = (t.task_type && t.task_type.name) || 'Task';
      return t.status && t.status.id === 4 ? `${name} (snoozed)` : name;
    },
    dueDate(p) {
      const t = this.activeTask(p);
      if (!t || !t.due_date) return null;
      const date = this.fmtDate(t.due_date);
      if (!t.due_time) return date;
      return `${date} at ${this.formatDueTime(t.due_time, t.due_date)}`;
    },
    isOverdue(p) {
      const t = this.activeTask(p);
      return !!(t && t.is_overdue && !t.is_inactive);
    },
    fmtDate(raw) {
      if (!raw) return null;
      let s = String(raw).trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) s += 'T00:00:00';
      else if (s.includes(' ')) s = s.replace(' ', 'T');
      const d = new Date(s);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
    formatDueTime,
  },
};
</script>
